import { semanticColors } from '@/common/styles/colorTokens';
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { typography } from '@/common/styles/typographyTokens';
import { formatNumberWithCommas } from '@/common/utils/format';

type InputType = 
  | 'text'
  | 'password'        // 비밀번호
  | 'businessNumber'  // 사업자번호
  | 'residentNumber'  // 주민등록번호
  | 'phone'          // 핸드폰번호
  | 'number'         // 숫자만
  | 'numberWithComma' // 숫자 + 콤마
  | 'date'          // 날짜
  | 'website'       // 웹사이트 URL
  | 'percent'       // 퍼센트
  | 'month';        // 월

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  type?: InputType;
  maxWidth?: number;
  error?: boolean;
  errorMessage?: string;
  readOnly?: boolean;
  align?: 'left' | 'right' | 'center';
  dateFormat?: string; // 날짜 형식 (기본값: YYYY-MM-DD)
  max?: number;
  onChangeData?: (data: string) => void;
  color?: string; // 특정 색상으로 표시할 때 사용
  onBlurData?: (data: string) => void;
  allowNegative?: boolean; // 음수 허용 여부
}


const Input = forwardRef<HTMLInputElement, InputProps>(({ 
  type = 'text',
  value,
  onChange,
  error,
  maxWidth,
  readOnly,
  dateFormat = 'YYYY-MM-DD',
  align = 'left',
  errorMessage,
  max,
  onChangeData,
  color,
  onBlurData,
  allowNegative = false,
  ...props 
}, ref) => {

  const formatValue = (val: string): string => {
    // password 타입일 경우 포맷팅하지 않고 그대로 반환
    if (type === 'password') {
      return val;
    }

    // date 타입일 경우 날짜 포맷팅
    if (type === 'date') {
      // 숫자와 하이픈만 허용
      let formatted = val.replace(/[^\d-]/g, '');
      
      // YYYY-MM-DD 형식으로 포맷팅
      if (dateFormat === 'YYYY-MM-DD') {
        formatted = formatted.replace(/(\d{4})(\d{1,2})?(\d{1,2})?/, function(_, year, month, day) {
          let parts = [year];
          if (month) parts.push(month.padStart(2, '0'));
          if (day) parts.push(day.padStart(2, '0'));
          return parts.join('-');
        });
        
        // 날짜 유효성 검사 및 제한
        const parts = formatted.split('-');
        if (parts.length > 1 && parts[1].length === 2) {
          const month = parseInt(parts[1]);
          if (month > 12) parts[1] = '12';
          if (month < 1 && parts[1] !== '00') parts[1] = '01';
        }
        
        if (parts.length > 2 && parts[2].length === 2) {
          const day = parseInt(parts[2]);
          const month = parseInt(parts[1]);
          const year = parseInt(parts[0]);
          
          let maxDay = 31;
          if ([4, 6, 9, 11].includes(month)) maxDay = 30;
          else if (month === 2) {
            // 윤년 계산
            maxDay = ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) ? 29 : 28;
          }
          
          if (day > maxDay) parts[2] = maxDay.toString();
          if (day < 1 && parts[2] !== '00') parts[2] = '01';
        }
        
        formatted = parts.join('-');
      }
      
      return formatted.slice(0, 10); // YYYY-MM-DD
    }

    if(type === 'month'){
      let formatted = val.replace(/[^0-9]/g, ''); // 숫자만 추출
      return formatted.slice(0, 6); // YYYY-MM
    }
    
    // website 타입일 경우 URL 포맷팅 처리
    if (type === 'website') {
      if (!val || val.trim() === '') return val;
      
      const trimmedUrl = val.trim();
      // 프로토콜이 없으면 https:// 추가
      if (!/^https?:\/\//i.test(trimmedUrl)) {
        return `https://${trimmedUrl}`;
      }
      
      return trimmedUrl;
    }

    // percent 타입일 경우 숫자와 소수점 처리
    if (type === 'percent') {
      // 숫자와 소수점만 허용
      let formatted = val.replace(/[^0-9.]/g, '');

      // 소수점이 두 개 이상 있으면 첫 번째 소수점만 남김
      const dotIndex = formatted.indexOf('.');
      if (dotIndex !== -1) {
        const beforeDot = formatted.substring(0, dotIndex + 1);
        const afterDot = formatted.substring(dotIndex + 1).replace(/\./g, '');
        formatted = beforeDot + afterDot;
      }

      // 값이 있을 경우 퍼센트 기호 추가
      if (formatted) {
        // 퍼센트 값이 max를 초과하면 max로 제한
        if (max !== undefined && parseFloat(formatted) > max) {
          formatted = max.toString();
        }
        return formatted;
      }
      return formatted;
    }

    // numberWithComma 타입에서 음수 허용 처리를 먼저 확인
    let isNegative = false;
    if (type === 'numberWithComma' && allowNegative && val.startsWith('-')) {
      isNegative = true;
    }

    // 음수인 경우 '-' 기호 제거 후 숫자만 추출, 아닌 경우 전체에서 숫자만 추출
    let formatted = isNegative ? val.substring(1).replace(/[^0-9]/g, '') : val.replace(/[^0-9]/g, '');

    switch (type) {
      case 'businessNumber':
        if (formatted.length <= 10) {
          formatted = formatted.replace(/(\d{3})(\d{2})?(\d{5})?/, function(_, p1, p2, p3) {
            let parts = [p1];
            if (p2) parts.push(p2);
            if (p3) parts.push(p3);
            return parts.join('-');
          });
        }
        return formatted.slice(0, 12); // xxx-xx-xxxxx

      case 'residentNumber':
        if (formatted.length <= 13) {
          formatted = formatted.replace(/(\d{6})(\d{7})?/, function(_, p1, p2) {
            let parts = [p1];
            if (p2) parts.push(p2);
            return parts.join('-');
          });
        }
        return formatted.slice(0, 14); // xxxxxx-xxxxxxx

      case 'phone':
        if (formatted.length <= 11) {
          formatted = formatted.replace(/(\d{3})(\d{4})?(\d{4})?/, function(_, p1, p2, p3) {
            let parts = [p1];
            if (p2) parts.push(p2);
            if (p3) parts.push(p3);
            return parts.join('-');
          });
        }
        return formatted.slice(0, 13); // xxx-xxxx-xxxx
        
      case 'numberWithComma':
        const result = formatNumberWithCommas(Number(formatted));
        return isNegative ? '-' + result : result;
        
      case 'number':
        return max ? Number(formatted) > Number(max) ? max.toString() : formatted : formatted;

      default:
        return val;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = formatValue(e.target.value);
    
    // numberWithComma 타입의 경우 clean한 숫자 값을 전달
    let dataValue = newValue;
    if (type === 'numberWithComma') {
      const cleanNumber = e.target.value.replace(/[^0-9]/g, '');
      dataValue = cleanNumber;
    }
    
    onChange?.(e);
    onChangeData?.(dataValue);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const newValue = formatValue(e.target.value);
    
    
    // website 타입인 경우 포맷팅된 값을 onChange로 반영
    if (type === 'website' && newValue !== e.target.value && onChange) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: newValue
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(syntheticEvent);
    }
    
    onBlurData?.(newValue);
  };

  // 퍼센트 타입일 때 표시되는 값에 % 기호 추가
  const displayValue = (): string | number | readonly string[] | undefined => {
    if(type === 'numberWithComma') {
      const strValue = String(value || '');
      // 빈 값이거나 "-"만 있는 경우 그대로 반환
      if (strValue === '' || strValue === '-') {
        return strValue;
      }
      const numValue = Number(strValue);
      // NaN인 경우 원본 값 반환
      if (isNaN(numValue)) {
        return strValue;
      }
      return formatNumberWithCommas(numValue);
    }
    return value;
  };

  return (
    <Container style={{maxWidth: maxWidth}}>
      <StyledInput
        className='body-regular-16'
        ref={ref}
        value={displayValue()}
        type={type === 'password' ? 'password' : 
              type === 'date' ? 'date' : 
              type === 'month' ? 'month' : 
              type === 'percent' ? 'number' : 'text'}
        onChange={handleChange}
        onBlur={handleBlur}
        $error={error}
        $maxWidth={maxWidth}
        $readOnly={readOnly}
        $isDate={type === 'date' || type === 'month'}
        $isPercent={type === 'percent'}
        $align={align}
        $color={color}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        max={type === 'percent' ? 100 : type === 'date' ? "9999-12-31" : type === 'month' ? "9999-12" : max}
        {...props}
      />
      {(error && errorMessage) && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Container>
  );
});

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ErrorMessage = styled.span`
  color: ${semanticColors.text.onDanger.secondary};
  ${typography.caption.caption2}
`;

const StyledInput = styled.input<{ 
  $error?: boolean, 
  $maxWidth?: number, 
  $readOnly?: boolean,
  $isDate?: boolean,
  $align?: 'left' | 'right' | 'center',
  $color?: string,
  $isPercent?: boolean,
}>`
  width: 100%;
  height: 3rem;
  max-width: ${props => props.$maxWidth ? `${props.$maxWidth}px` : '100%'};
  padding: 0 0.75rem;
  border: 1px solid ${props => 
    props.$error ? semanticColors.border.dangerPrimary : 
    props.$color ? props.$color : 
    semanticColors.border.primary};
  border-radius: 7px;
  color: ${props => props.$color ? props.$color : semanticColors.text.primary};
  transition: all 0.2s;
  background-color: ${semanticColors.background.secondary};
  pointer-events: ${props => props.$readOnly ? 'none' : 'auto'};
  cursor: auto;
  text-align: ${props => props.$align};

  &:read-only {
    border: none;
  }

  &:focus {
    outline: none;
    border: 2px solid ${semanticColors.border.brand};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${semanticColors.text.disabled};
  }

  &[type="number"] {
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  ${props => props.$isPercent && `
    position: relative;
    
    &::after {
      content: '%';
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      color: ${semanticColors.text.primary};
      font-size: 1rem;
      pointer-events: none;
    }
  `}
`;


export { Input };